// Address autocomplete backed by the Lasso backend (`data/adressevaelger/*` + `data/dar/lookup`), which replaces DAWA's retired
// `/autocomplete` endpoint. The backend proxies Adressevælgeren (Klimadatastyrelsen's official DAWA
// replacement): `autocomplete` returns lean suggestions, and `lookup` resolves a selected suggestion's
// structured fields + coordinates via DAR. See dawa-migration-plan-mwk.md.
//
// The consuming apps differ in only two ways, so both are supplied by the caller:
//   - how they reach the backend (embedded apps go through the Lasso app SDK, the portal through its own
//     axios instance) -> pass an `apiGet` to `createAddressAutocomplete()`
//   - which `Address` class they build -> feed `createAddressFields()` into your own `new Address(...)`

export interface AddressSuggestionBase {
  type: string
  tekst: string
  forslagstekst: string
  caretpos: number
}

export interface AddressSuggestionData {
  id: string
}

export interface StreetNameSuggestion extends AddressSuggestionBase {
  type: 'vejnavn'
}

export interface AccessAddressSuggestion extends AddressSuggestionBase {
  type: 'adgangsadresse'
  data: AddressSuggestionData
}

export interface FullAddressSuggestion extends AddressSuggestionBase {
  type: 'adresse'
  data: AddressSuggestionData
}

export type AddressSuggestion = (
  | StreetNameSuggestion
  | AccessAddressSuggestion
  | FullAddressSuggestion
)

/**
 * Field set accepted by each app's `Address` constructor. Apps whose `Address` has no `cityName`
 * (e.g. the portal's shared virk Address) simply ignore that field.
 */
export interface AddressFields {
  coName: null
  streetName: string | null
  houseNumberFrom: number | null
  houseLetterFrom: string | null
  houseNumberTo: null
  houseLetterTo: null
  floor: string | null
  sideDoor: string | null
  cityName: string | null
  postalCode: number | null
  postalDistrict: string | null
  coordinates: { latitude: string, longitude: string } | null
}

/** An item from the backend's `data/adressevaelger/autocomplete`. */
interface BackendAutocompleteSuggestion {
  type: string // Adressevælger type, e.g. 'vejnavn' | 'husnummer' | 'adresse'
  tekst: string
  id: string | null
  accessAddressId: string | null
}

/** The backend's `data/dar/lookup` response (a DAWAAddress). */
interface BackendLookupAddress {
  streetName: string | null
  houseNo: string | null
  floor: string | null
  door: string | null
  additionalCity: string | null
  postalCode: string | null
  city: string | null // postal district
  accessPoint: { latitude: number, longitude: number } | null
}

/** Performs a GET against the Lasso backend and resolves the parsed body. `path` has no leading slash. */
export type AddressApiGet = <T>(path: string) => Promise<T>

function parseIntOrNull (input: string | null) {
  if (!input) return null
  const int = parseInt(input)
  if (isNaN(int)) return null
  return int
}

function mapType (adressevaelgerType: string): AddressSuggestion['type'] {
  switch (adressevaelgerType) {
    case 'adresse': return 'adresse'
    case 'husnummer':
    case 'adgangsadresse': return 'adgangsadresse'
    default: return 'vejnavn'
  }
}

export function createAddressAutocomplete (apiGet: AddressApiGet) {
  const cache = new Map<string, Promise<AddressSuggestion[]>>()

  function loadSuggestions (options: {
    searchString: string
    caretPos?: number
    accessAddressId?: string
  }): Promise<AddressSuggestion[]> {
    const searchString = options.searchString

    let promise = cache.get(searchString)

    if (!promise) {
      promise = apiGet<BackendAutocompleteSuggestion[]>(
        'data/adressevaelger/autocomplete?q=' + encodeURIComponent(searchString),
      ).then(results => (results ?? []).map((result): AddressSuggestion => {
        const type = mapType(result.type)
        const base = { tekst: result.tekst, forslagstekst: result.tekst, caretpos: result.tekst.length }

        if (type === 'vejnavn') return { ...base, type }

        // For an access address the id to resolve later is the husnummer id; for a full address it is
        // the address id itself.
        const id = (type === 'adgangsadresse' ? (result.accessAddressId ?? result.id) : result.id) ?? ''
        return { ...base, type, data: { id } }
      }))

      cache.set(searchString, promise)
    }

    return promise
  }

  async function createAddressFields (
    suggestion: AccessAddressSuggestion | FullAddressSuggestion,
  ): Promise<AddressFields> {
    // An adgangsadresse suggestion carries a husnummer (access address) id, which the backend resolves
    // through a different lookup than a full address id — sending it as `id` would return null.
    const idParameter = suggestion.type === 'adgangsadresse' ? 'accessAddressId' : 'id'

    const lookup = await apiGet<BackendLookupAddress>(
      `data/dar/lookup?${idParameter}=` + encodeURIComponent(suggestion.data.id),
    )

    let houseNumberFrom: number | null = null
    let houseLetterFrom: string | null = null

    if (lookup?.houseNo) {
      const matches = lookup.houseNo.match(/^([0-9]*)([A-z]*)$/)
      if (matches) {
        houseNumberFrom = parseIntOrNull(matches[1])
        houseLetterFrom = matches[2] || null
      }
    }

    // `|| null` rather than `?? null` on the text fields: the Datafordeler-backed data uses "" for
    // absent values, and an empty string would otherwise show up as a stray address separator.
    return {
      coName: null,

      streetName: lookup?.streetName || null,

      houseNumberFrom,
      houseLetterFrom,
      houseNumberTo: null,
      houseLetterTo: null,

      floor: lookup?.floor || null,
      sideDoor: lookup?.door || null,

      cityName: lookup?.additionalCity || null,

      postalCode: parseIntOrNull(lookup?.postalCode ?? null),
      postalDistrict: lookup?.city || null,

      coordinates: lookup?.accessPoint
        ? {
          latitude: lookup.accessPoint.latitude.toString(),
          longitude: lookup.accessPoint.longitude.toString(),
        }
        : null,
    }
  }

  return { loadSuggestions, createAddressFields }
}
